import { useCallback } from "react";

import ExpoTvosTopShelf from "$modules/expo-tvos-top-shelf";
import { APP_APP_GROUP, APP_SCHEME } from "$src/core/constants/env";
import useTranslation from "$src/core/hooks/use-translation";
import type { PageDto } from "$src/core/models/dto";
import Logging from "$src/core/services/logging/logging";
import isMovieContent from "$src/core/utils/dto/is-movie-content";
import isSeriesContent from "$src/core/utils/dto/is-series-content";

const useTvosTopShelf = () => {
  const { t } = useTranslation();

  const pushContent = useCallback(
    (storefrontData: PageDto) => {
      const movieItems =
        storefrontData.sections
          ?.flatMap((item) => item.items)
          .filter((item) => isMovieContent(item)) ?? [];

      const seriesItems =
        storefrontData.sections
          ?.flatMap((item) => item.items)
          .filter((item) => isSeriesContent(item)) ?? [];

      const topShelfMovieItems = movieItems.map((item) => ({
        identifier: item.id.toString(),
        title: item.title,
        imageURL:
          item.image?.landscape?.url ?? item.images?.landscape?.url ?? "",
        imageShape: "extraWide" as const,
        displayURL: `${APP_SCHEME}://${item.type}/${item.id}`,
        playURL: `${APP_SCHEME}://${item.type}/${item.id}/play`,
        previewVideoURL: item.playback?.sources.at(0)?.url,
      }));

      const topShelfSeriesItems = seriesItems.map((item) => ({
        identifier: item.id.toString(),
        title: item.title,
        imageURL:
          item.image?.landscape?.url ?? item.images?.landscape?.url ?? "",
        imageShape: "extraWide" as const,
        displayURL: `${APP_SCHEME}://${item.type}/${item.id}`,
        playURL: `${APP_SCHEME}://${item.type}/${item.id}/play`,
      }));

      const topShelfSections = [
        {
          identifier: "movies",
          title: t("COMMON/MOVIES"),
          items: topShelfMovieItems,
        },
        {
          identifier: "series",
          title: t("COMMON/SERIES"),
          items: topShelfSeriesItems,
        },
      ].filter((section) => section.items.length);

      ExpoTvosTopShelf.setContentAsync(
        {
          style: "sectioned",
          carouselStyle: "actions",
          sections: topShelfSections,
        },
        APP_APP_GROUP,
      ).catch(Logging.error);
    },
    [t],
  );

  return {
    pushContent,
  };
};

export default useTvosTopShelf;
